import { apiRequest } from '../../../shared/api';
import {
  SafetyAction,
  SafetyActionDashboardStats,
  ActionStatus,
  ActionType,
  ActionPriority,
  SafetyActionExtension,
  SafetyActionVerification,
  SafetyActionEffectivenessReview,
  SafetyActionComment,
  SafetyActionEvidence,
} from './types';

export interface ActionListResponse {
  items: SafetyAction[];
  total: number;
}

export const actionService = {
  // Dashboard & Analytics
  getDashboardStats: (organizationId: string = 'ORG-XYZ') =>
    apiRequest<SafetyActionDashboardStats>(`/actions/analytics/dashboard?organizationId=${organizationId}`),

  // Action Register List
  getActions: (params: {
    organizationId?: string;
    status?: ActionStatus | ActionStatus[];
    actionType?: ActionType;
    priority?: ActionPriority;
    assigneeId?: string;
    department?: string;
    sourceModule?: string;
    sourceId?: string;
    isOverdue?: boolean;
    searchTerm?: string;
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.organizationId) query.append('organizationId', params.organizationId);
    if (params.status) {
      if (Array.isArray(params.status)) {
        params.status.forEach((s) => query.append('status', s));
      } else {
        query.append('status', params.status);
      }
    }
    if (params.actionType) query.append('actionType', params.actionType);
    if (params.priority) query.append('priority', params.priority);
    if (params.assigneeId) query.append('assigneeId', params.assigneeId);
    if (params.department) query.append('department', params.department);
    if (params.sourceModule) query.append('sourceModule', params.sourceModule);
    if (params.sourceId) query.append('sourceId', params.sourceId);
    if (params.isOverdue !== undefined) query.append('isOverdue', params.isOverdue ? 'true' : 'false');
    if (params.searchTerm) query.append('searchTerm', params.searchTerm);
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.offset) query.append('offset', params.offset.toString());

    return apiRequest<ActionListResponse>(`/actions?${query.toString()}`);
  },

  // Detail
  getActionById: (id: string) => apiRequest<SafetyAction>(`/actions/${id}`),

  // Create
  createAction: (data: Partial<SafetyAction> & { dueDate: string }) =>
    apiRequest<SafetyAction>('/actions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Update
  updateAction: (id: string, data: Partial<SafetyAction>) =>
    apiRequest<SafetyAction>(`/actions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Status transition
  transitionStatus: (id: string, toStatus: ActionStatus, reason: string, changedBy?: string) =>
    apiRequest<SafetyAction>(`/actions/${id}/transition`, {
      method: 'POST',
      body: JSON.stringify({ toStatus, reason, changedBy }),
    }),

  // Reassignment
  assignAction: (
    id: string,
    toAssigneeId: string,
    toAssigneeName: string,
    toDepartment?: string,
    reassignmentReason?: string,
    assignedBy?: string
  ) =>
    apiRequest<SafetyAction>(`/actions/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({
        toAssigneeId,
        toAssigneeName,
        toDepartment,
        reassignmentReason,
        assignedBy,
      }),
    }),

  // Complete
  completeAction: (id: string, completionNotes: string, completedBy: string) =>
    apiRequest<SafetyAction>(`/actions/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ completionNotes, completedBy }),
    }),

  // Independent Verification (SoD)
  verifyAction: (
    id: string,
    data: {
      verifierId: string;
      verifierName: string;
      isVerified: boolean;
      evidenceNotes: string;
      signoffRole: string;
    }
  ) =>
    apiRequest<SafetyActionVerification>(`/actions/${id}/verifications`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Effectiveness Review
  recordEffectivenessReview: (
    id: string,
    data: {
      reviewerId: string;
      reviewerName: string;
      methodology: string;
      isEffective: boolean;
      residualImpact: string;
      notes?: string;
    }
  ) =>
    apiRequest<SafetyActionEffectivenessReview>(`/actions/${id}/effectiveness`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Extensions (Deadline Integrity Rule)
  requestExtension: (id: string, requestedDueDate: string, reason: string, requestedBy: string) =>
    apiRequest<SafetyActionExtension>(`/actions/${id}/extensions`, {
      method: 'POST',
      body: JSON.stringify({ requestedDueDate, reason, requestedBy }),
    }),

  decideExtension: (
    id: string,
    extensionId: string,
    approved: boolean,
    decisionNotes: string,
    decidedBy: string
  ) =>
    apiRequest<SafetyActionExtension>(`/actions/${id}/extensions/${extensionId}`, {
      method: 'PUT',
      body: JSON.stringify({ approved, decisionNotes, decidedBy }),
    }),

  // Bulk Reassign
  bulkReassign: (data: {
    actionIds: string[];
    toAssigneeId: string;
    toAssigneeName: string;
    toDepartment?: string;
    reason?: string;
    assignedBy?: string;
  }) =>
    apiRequest<{ updatedCount: number }>('/actions/bulk/reassign', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Comments & Evidence
  addComment: (id: string, commentText: string, userId?: string, userName?: string) =>
    apiRequest<SafetyActionComment>(`/actions/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ commentText, userId, userName }),
    }),

  addEvidence: (
    id: string,
    data: {
      fileName: string;
      fileType: string;
      fileSize: number;
      storagePath: string;
      uploadedBy?: string;
    }
  ) =>
    apiRequest<SafetyActionEvidence>(`/actions/${id}/evidence`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};
